<template>
  <v-dialog :value="true" max-width="720" persistent scrollable>
    <v-card>
      <v-card-title>{{ resx.labels.files }} — {{ obligation.name }}</v-card-title>
      <v-card-text>
        <!-- Navegador de periodo. La flecha derecha se frena en el mes actual:
             no hay comprobantes del futuro. -->
        <div v-if="!showAll" class="d-flex align-center justify-center mb-2">
          <v-btn icon :disabled="loading" @click="shift(-1)"><v-icon>chevron_left</v-icon></v-btn>
          <span class="subtitle-1 mx-4">{{ periodLabel(period) }}</span>
          <v-btn icon :disabled="loading || period >= currentPeriod()" @click="shift(1)"><v-icon>chevron_right</v-icon></v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>

        <div v-if="showAll">
          <div v-for="group in groups" :key="group.period" class="mb-3">
            <div class="subtitle-2 grey--text text--darken-1 mb-1">{{ periodLabel(group.period) }}</div>
            <v-list dense>
              <v-list-item v-for="file in group.files" :key="file._id">
                <v-list-item-content>
                  <v-list-item-title>{{ file.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ file.originalName }} · {{ formatSize(file.size) }} · {{ formatDate(file.uploadedAt) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row">
                  <v-btn icon :title="resx.labels.open_file" @click="open(file)"><v-icon color="primary">open_in_new</v-icon></v-btn>
                  <v-btn icon :disabled="saving" @click="remove(file)"><v-icon color="error">delete</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
          <div v-if="!groups.length && !loading" class="body-2 grey--text">{{ resx.labels.no_files_in_period }}</div>
        </div>

        <div v-else>
          <v-list dense v-if="periodFiles.length">
            <v-list-item v-for="file in periodFiles" :key="file._id">
              <v-list-item-content>
                <v-list-item-title>{{ file.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ file.originalName }} · {{ formatSize(file.size) }} · {{ formatDate(file.uploadedAt) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row">
                <v-btn icon :title="resx.labels.open_file" @click="open(file)"><v-icon color="primary">open_in_new</v-icon></v-btn>
                <v-btn icon :disabled="saving" @click="remove(file)"><v-icon color="error">delete</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else-if="!loading" class="body-2 grey--text">{{ resx.labels.no_files_in_period }}</div>
        </div>

        <v-btn text small class="mt-1" @click="showAll = !showAll">
          {{ showAll ? resx.labels.view_by_period : resx.labels.view_all_files }}
        </v-btn>

        <v-divider class="my-4" />

        <!-- Carga. El nombre se completa con el del archivo sin extension y se
             puede cambiar; el periodo arranca en el que se esta viendo. -->
        <div class="subtitle-2 mb-2">{{ resx.labels.upload_file }}</div>
        <v-file-input
          v-model="upload.file"
          :label="resx.labels.upload_file"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.odt,.ods"
          prepend-icon="attach_file"
          show-size
          :disabled="saving"
          @change="onFileChosen"
        />
        <v-text-field v-model="upload.name" :label="resx.labels.file_name" maxlength="100" counter :disabled="saving" />
        <v-menu v-model="periodMenu" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="periodLabel(upload.period)"
              :label="resx.labels.period"
              readonly
              v-on="on"
              :disabled="saving"
              prepend-icon="event"
            />
          </template>
          <v-date-picker :value="upload.period" type="month" :max="currentPeriod()" @input="onPeriodPicked" />
        </v-menu>
        <v-btn class="primary" :loading="saving" :disabled="!upload.file || !upload.name || saving" @click="submit">
          {{ resx.labels.upload }}
        </v-btn>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="saving" @click="$emit('close')">{{ resx.labels.close }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import moment from "moment";
import contractorCompanyService from "@/services/ContractorCompanyService";
import { currentPeriod, shiftPeriod, periodLabel, groupByPeriod, formatSize } from "@/utils/period";

export default {
  name: "obligation-files-dialog",
  props: {
    obligation: { type: Object, required: true },
    contractorCompanyId: { type: String, required: true },
    // Periodo (YYYY-MM) con el que abre; null = mes actual.
    initialPeriod: { type: String, default: null }
  },
  data() {
    return {
      files: [],
      period: this.initialPeriod || currentPeriod(),
      showAll: false,
      loading: false,
      saving: false,
      errorMessage: null,
      periodMenu: false,
      // Un objeto para el formulario de carga entero: al terminar se
      // reemplaza de una, y asi v-file-input queda limpio.
      upload: { file: null, name: "", period: this.initialPeriod || currentPeriod() },
      // Si hubo alguna subida o borrado, la pantalla recarga el resumen al cerrar.
      changed: false
    };
  },
  computed: {
    periodFiles() {
      return this.files.filter(f => f.period === this.period);
    },
    groups() {
      return groupByPeriod(this.files);
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    currentPeriod,
    periodLabel,
    formatSize,
    formatDate(value) {
      return moment(value).format("DD/MM/YYYY");
    },
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async load() {
      this.loading = true;
      this.errorMessage = null;
      try {
        this.files = await contractorCompanyService.listObligationFiles(this.contractorCompanyId, this.obligation);
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar los archivos");
      } finally {
        this.loading = false;
      }
    },
    shift(delta) {
      this.period = shiftPeriod(this.period, delta);
      this.upload.period = this.period;
    },
    onFileChosen(file) {
      if (file && !this.upload.name) {
        this.upload.name = file.name.replace(/\.[^.]+$/, "");
      }
    },
    // v-date-picker type=month devuelve 'YYYY-MM'.
    onPeriodPicked(value) {
      this.upload.period = value;
      this.periodMenu = false;
    },
    async submit() {
      if (!this.upload.file || !this.upload.name || this.saving) return;
      this.saving = true;
      this.errorMessage = null;
      try {
        const created = await contractorCompanyService.uploadObligationFile(
          this.contractorCompanyId, this.obligation, this.upload);
        this.files = [created].concat(this.files);
        // La vista salta al periodo del archivo subido, para que se vea.
        this.period = created.period;
        this.showAll = false;
        this.upload = { file: null, name: "", period: created.period };
        this.changed = true;
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo subir el archivo");
      } finally {
        this.saving = false;
      }
    },
    async open(file) {
      this.errorMessage = null;
      try {
        const blob = await contractorCompanyService.downloadObligationFile(this.contractorCompanyId, file._id);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        // El navegador ya tiene la pestaña: se libera el objeto un rato despues.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo abrir el archivo");
      }
    },
    async remove(file) {
      if (!window.confirm(this.resx.labels.delete_file_confirm)) return;
      this.saving = true;
      this.errorMessage = null;
      try {
        await contractorCompanyService.deleteObligationFile(this.contractorCompanyId, file._id);
        this.files = this.files.filter(f => f._id !== file._id);
        this.changed = true;
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo borrar el archivo");
      } finally {
        this.saving = false;
      }
    }
  },
  beforeDestroy() {
    if (this.changed) this.$emit("changed");
  }
};
</script>
